import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  typingReducer,
  createInitialTypingState,
  isComplete,
  type TypingState,
} from '../engine/TypingEngine';
import { calculateMetrics } from '../engine/MetricsCalculator';
import { preprocessText } from '../engine/TextPreprocessor';
import { useTypingStore } from '../store/useTypingStore';

/**
 * Core session hook.
 *
 * Keystroke path:
 *   keydown -> typingStateRef mutation (O(1), no React) -> completion check
 *
 * Metrics path (decoupled):
 *   RAF loop (60fps max) -> read ref -> calculateMetrics -> setMetrics (Zustand)
 *
 * This separation ensures keystrokes never trigger React reconciliation directly.
 */
/**
 * Optional callback invoked on every keystroke (before state update).
 * Passed as a ref so it never causes the hook to re-render.
 * Use this for side-effects like heatmap tracking — NOT for driving UI state.
 */
export type KeystrokeCallback = (key: string) => void;

export function useTypingSession(
  source: string | null,
  onKeystrokeRef?: RefObject<KeystrokeCallback | null>,
) {
  const { phase, setPhase, setMetrics, reset } = useTypingStore();

  // Hot path: lives in a ref, never in React/Zustand state
  const typingStateRef = useRef<TypingState | null>(null);
  const startTimestampRef = useRef<number>(0);
  const rafIdRef = useRef<number>(0);

  // Lightweight render trigger — incremented once per keystroke.
  // This is the ONLY React state on the hot path. It busts LineRow's memo
  // so getCharStatus is re-called, then CharacterCell.memo does the real
  // diffing — only 2-3 cells commit to the DOM per keystroke.
  const [renderTick, setRenderTick] = useState(0);

  // Initialize state when source changes
  useEffect(() => {
    if (!source) return;
    const chars = preprocessText(source);
    typingStateRef.current = createInitialTypingState(chars);
    reset();
  }, [source, reset]);

  // RAF loop — runs only during active phase
  useEffect(() => {
    if (phase !== 'active') {
      cancelAnimationFrame(rafIdRef.current);
      return;
    }

    const tick = () => {
      const state = typingStateRef.current;
      if (!state) return;
      const m = calculateMetrics(
        state.currentIndex,
        state.errors.size,
        startTimestampRef.current,
      );
      setMetrics(m.wpm, m.cpm, m.accuracy, m.errorCount, m.elapsedMs, state.currentIndex);
      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafIdRef.current);
  }, [phase, setMetrics]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!typingStateRef.current || !source) return;

      // Ignore modifier-only keys and browser shortcuts
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const { key } = e;

      if (key === 'Tab') {
        e.preventDefault();
        onKeystrokeRef?.current?.('Tab');
        let state = typingStateRef.current;
        for (let i = 0; i < 4; i++) {
          state = typingReducer(state, { type: 'KEY_PRESS', key: ' ' });
        }
        typingStateRef.current = state;
        setRenderTick((t) => t + 1);
        if (phase === 'idle') {
          startTimestampRef.current = performance.now();
          setPhase('active');
        }
        if (isComplete(state)) setPhase('complete');
        return;
      }

      if (key === 'Backspace') {
        e.preventDefault();
        onKeystrokeRef?.current?.('Backspace');
        typingStateRef.current = typingReducer(typingStateRef.current, {
          type: 'BACKSPACE',
        });
        setRenderTick((t) => t + 1);
        return;
      }

      // Only process printable single characters and Enter
      if (key.length !== 1 && key !== 'Enter') return;

      const char = key === 'Enter' ? '\n' : key;
      onKeystrokeRef?.current?.(key);

      if (phase === 'idle') {
        startTimestampRef.current = performance.now();
        setPhase('active');
      }

      typingStateRef.current = typingReducer(typingStateRef.current, {
        type: 'KEY_PRESS',
        key: char,
      });
      setRenderTick((t) => t + 1);

      if (isComplete(typingStateRef.current)) {
        setPhase('complete');
      }
    },
    [source, phase, setPhase],
  );

  const restart = useCallback(() => {
    if (!source) return;
    const chars = preprocessText(source);
    typingStateRef.current = createInitialTypingState(chars);
    setRenderTick(0);
    reset();
  }, [source, reset]);

  /**
   * Returns a CharStatus for a given global char index.
   * Called by CharacterCell via a stable ref — does NOT cause renders.
   */
  const getCharStatus = useCallback(
    (index: number): 'pending' | 'correct' | 'incorrect' | 'active' => {
      const state = typingStateRef.current;
      if (!state) return 'pending';
      if (index === state.currentIndex) return 'active';
      if (index > state.currentIndex) return 'pending';
      return state.errors.has(index) ? 'incorrect' : 'correct';
    },
    [],
  );

  // Expose a ref to the typing state for components that need cursor position
  const getTypingState = useCallback((): TypingState | null => {
    return typingStateRef.current;
  }, []);

  return { handleKeyDown, restart, getCharStatus, getTypingState, phase, renderTick };
}
